import { ReactNode } from 'react';

export interface RiskBadgeProps {
  /** The percentage value to display in the badge */
  value: number | string;
  /** The color variant for the badge - can be CSS class or variant */
  variant?: 'success' | 'warning' | 'error';
  /** CSS class for styling (alternative to variant) */
  colorClass?: string;
  /** Optional additional CSS classes */
  className?: string;
  /** Optional icon to display before the value (for risk change badges) */
  icon?: ReactNode;
  /** Whether to show the percentage symbol */
  showPercent?: boolean;
  /** Custom styling for different badge types */
  badgeType?: 'risk' | 'change';
}

/**
 * A reusable risk badge component that displays a percentage value
 * with appropriate color coding based on risk level.
 *
 * Supports two main use cases:
 * 1. Risk percentage badges (for top-3-risks component)
 * 2. Risk change badges with icons (for intervention cards, etc)
 */
export default function RiskBadge({
  value,
  variant,
  colorClass,
  className = '',
  icon,
  showPercent = true,
  badgeType = 'risk'
}: RiskBadgeProps) {
  const getVariantClasses = () => {
    if (colorClass) {
      return colorClass;
    }

    switch (variant) {
      case 'success':
        return 'alert-success';
      case 'warning':
        return 'risk-badge-yellow';
      case 'error':
        return 'alert-error';
      default:
        return 'risk-badge-yellow';
    }
  };

  const getTextColorClass = () => {
    switch (variant) {
      case 'success':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-error';
      default:
        return 'text-warning';
    }
  };

  const getContainerClasses = () => {
    if (badgeType === 'change') {
      return `${getVariantClasses()} rounded-[4px] px-[5px] py-2.5 h-[25px] flex items-center justify-center gap-1 font-inter text-xs font-semibold leading-[1.3]`;
    }

    return `${getVariantClasses()} risk-badge`;
  };

  return (
    <div className={`${getContainerClasses()} ${className}`}>
      {icon && <span className="flex items-center justify-center gap-1 size-2.5 shrink-0">{icon}</span>}
      <span className={badgeType === 'risk' ? `body-small text-primary text-center font-semibold whitespace-nowrap ${getTextColorClass()}` : `text-center font-semibold whitespace-nowrap shrink-0 ${getTextColorClass()}`}>
        {value}{showPercent ? '%' : ''}
      </span>
    </div>
  );
}
